import { useParams } from "react-router-dom";
import { Link } from "@/i18n/LocaleLink";
import { ArrowRight, ArrowLeft, CalendarDays } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import NotFound from "@/pages/NotFound";
import { OFFERING_IMAGES, useContent } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitlistForm = ({ offerName, dateLabel, o2, index }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const { lang } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email, workshop: `${offerName} — ${dateLabel}`, lang });
      toast.success(data.message);
      setDone(true);
    } catch {
      toast.error(o2.waitlistError);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return <p className="mt-4 font-serif italic text-sm text-sage" data-testid={`waitlist-success-${index}`}>{o2.waitlistSuccess}</p>;
  }
  return (
    <form onSubmit={submit} className="mt-4 flex items-center border-b border-ink/25 focus-within:border-rust transition-colors duration-300" data-testid={`waitlist-form-${index}`}>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={o2.waitlistPlaceholder}
        className="flex-1 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-soft/50" data-testid={`waitlist-email-${index}`} />
      <button type="submit" disabled={sending} className="py-2 pl-3 text-xs tracking-wide text-rust disabled:opacity-50" data-testid={`waitlist-submit-${index}`}>
        {sending ? o2.waitlistSending : o2.joinWaitlist}
      </button>
    </form>
  );
};

export default function OfferPage() {
  const { slug } = useParams();
  const { ui, thresholds, offerings } = useContent();
  const offer = offerings.find((o) => o.slug === slug);
  useSeo({
    title: offer ? `${offer.name} | Women's Den` : "Women's Den",
    description: offer ? offer.blurb : "",
    image: OFFERING_IMAGES[slug],
  });
  if (!offer) return <NotFound />;
  const threshold = thresholds.find((t) => t.slug === offer.threshold);
  const others = offerings.filter((o) => o.slug !== slug && o.threshold === offer.threshold);
  const o2 = ui.offer;

  return (
    <main data-testid={`offer-page-${slug}`}>
      <PageHero eyebrow={offer.format} title={offer.name} image={OFFERING_IMAGES[slug]} testid="offer-hero" />

      <section className="bg-cream py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <Link to={`/thresholds/${threshold.slug}`} data-testid="offer-back-link"
              className="group inline-flex items-center gap-2 text-sm text-soft hover:text-clay transition-colors duration-300">
              <ArrowLeft size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-translate-x-1" />
              {o2.partOf} {threshold.name}
            </Link>
            <p className="mt-8 font-serif text-2xl sm:text-3xl leading-snug text-ink">{offer.blurb}</p>
            {offer.body.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-soft">{p}</p>
            ))}
            <Link
              to={`/contact?topic=${encodeURIComponent(offer.name)}`}
              data-testid="offer-book-button"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
            >
              {o2.askAbout} {offer.name}
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
            <div className="bg-parchment p-8 lg:sticky lg:top-28" data-testid="offer-practical">
              <p className="text-xs tracking-[0.3em] uppercase text-soft mb-6">{o2.practicalBits}</p>
              <dl className="space-y-5">
                {offer.practical.map(([k, v]) => (
                  <div key={k}>
                    <dt className="font-serif italic text-clay text-lg">{k}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-ink/80">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-8 border-t border-ink/10 pt-5 text-xs leading-relaxed text-soft">{o2.freeNote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {offer.dates && offer.dates.length > 0 && (
        <section className="bg-parchment/60 border-y border-ink/10 py-20" data-testid="offer-dates">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-3 text-clay">
                <CalendarDays size={18} strokeWidth={1.25} />
                <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{o2.upcomingDates}</h2>
              </div>
            </Reveal>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {offer.dates.map((d, i) => (
                <Reveal key={d.label} delay={i * 0.08} className="h-full">
                  <div className="flex h-full flex-col justify-between border border-ink/10 bg-cream p-7" data-testid={`offer-date-${i}`}>
                    <div>
                      <p className="font-serif text-2xl text-ink">{d.label}</p>
                      <p className="mt-2 text-sm leading-relaxed text-soft">{d.detail}</p>
                      {d.note && <p className="mt-3 font-serif italic text-sm text-clay/90" data-testid={`offer-date-note-${i}`}>{d.note}</p>}
                    </div>
                    {d.free === 0 ? (
                      <>
                        <span className="mt-6 self-start rounded-full border border-rust/40 bg-rust/10 px-4 py-1.5 text-xs tracking-wide text-rust" data-testid={`offer-date-spots-${i}`}>
                          {o2.soldOut}
                        </span>
                        <WaitlistForm offerName={offer.name} dateLabel={d.label} o2={o2} index={i} />
                      </>
                    ) : (
                      <span className="mt-6 self-start rounded-full border border-sage/40 bg-sage/10 px-4 py-1.5 text-xs tracking-wide text-sage" data-testid={`offer-date-spots-${i}`}>
                        {o2.placesFree.replace("{free}", d.free).replace("{total}", d.total)}
                      </span>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="bg-parchment py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{o2.alsoWithin}</h2>
            </Reveal>
            <div className="mt-10 grid gap-8 md:grid-cols-2">
              {others.map((o) => (
                <Reveal key={o.slug}>
                  <Link to={`/offerings/${o.slug}`} data-testid={`offer-related-${o.slug}`}
                    className="group flex items-center justify-between border-b border-ink/10 py-6">
                    <div>
                      <h3 className="font-serif text-2xl text-ink group-hover:text-clay transition-colors duration-300">{o.name}</h3>
                      <p className="mt-1 text-sm text-soft">{o.format}</p>
                    </div>
                    <ArrowRight size={20} strokeWidth={1.25} className="text-clay transition-transform duration-300 group-hover:translate-x-2" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
